import {
  Eye,
  HardDriveDownload,
  HardDriveUpload,
  PanelLeftClose,
  Plus,
  ScanEye,
  Trash2,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable, type ViewStyle } from "react-native";
import { ScrollView, Theme, View, XStack } from "tamagui";
import { useStores } from "../core/stores";
import { Body } from "../ui/Body";
import { Group } from "../ui/Group";
import { Label } from "../ui/Label";
import { Sheet } from "../ui/Sheet";
import { type DbInfo, DbInfoType } from "./DevsStore";

export const DevDbStats = observer(() => {
  const { devsStore } = useStores();

  const [style, setStyle] = React.useState<ViewStyle>({ opacity: 0.5 });
  const [visible, setIsVisible] = React.useState(false);
  const [filterItems, setFilterItems] = React.useState<DbInfo[]>([]);

  const doFilterItems = React.useCallback(
    (byType: DbInfoType) => {
      if (filterItems.length && filterItems[0].type === byType) {
        setFilterItems([]);
        return;
      }

      if (byType === DbInfoType.WatchAdded) {
        let activeWatches: DbInfo[] = [];
        devsStore.stats.forEach((s) => {
          if (s.type === DbInfoType.WatchAdded) {
            activeWatches.push(s);
          }
          if (s.type === DbInfoType.WatchRemoved) {
            activeWatches = activeWatches.filter((w) => {
              if (w.database === s.database && w.uid === s.uid) {
                return false;
              }
              return true;
            });
          }
        });

        setFilterItems(activeWatches);
      } else {
        setFilterItems(devsStore.stats.filter((s) => s.type === byType));
      }
    },
    [devsStore, filterItems, filterItems[0]],
  );

  if (!visible || !__DEV__) {
    return null;
  }

  return (
    <View
      animation={"medium"}
      style={style}
      onHoverIn={() => {
        setStyle({ opacity: 1 });
      }}
      onHoverOut={() => {
        setStyle({ opacity: 0.5 });
      }}
    >
      <XStack gap="$medium">
        <Theme name="dark">
          <ScrollView
            padding={"$medium"}
            backgroundColor={"$backgroundBlock"}
            horizontal
            alignItems="center"
          >
            <XStack gap="$large">
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.Add);
                }}
              >
                <Plus title="Adds" />
                <Body>{devsStore.adds}</Body>
              </XStack>
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.Get);
                }}
              >
                <HardDriveDownload title="Gets" />
                <Body>{devsStore.gets}</Body>
              </XStack>
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.Update);
                }}
              >
                <HardDriveUpload title="Updates" />
                <Body>{devsStore.updates}</Body>
              </XStack>
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.Set);
                }}
              >
                <HardDriveUpload title="Sets" />
                <Body>{devsStore.sets}</Body>
              </XStack>
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.WatchAdded);
                }}
              >
                <Eye title="Watches" />
                <Body>{devsStore.watchesActive}</Body>
              </XStack>
              <XStack
                gap={"$small"}
                onPress={() => {
                  doFilterItems(DbInfoType.WatchItem);
                }}
              >
                <ScanEye title="Watched items" />
                <Body>{devsStore.watchItems}</Body>
              </XStack>

              <Pressable
                onPress={() => {
                  setIsVisible(false);
                }}
              >
                <PanelLeftClose />
              </Pressable>

              <Pressable
                onPress={() => {
                  devsStore.clearStats();
                }}
              >
                <Trash2 />
              </Pressable>
            </XStack>
          </ScrollView>
        </Theme>

        {filterItems.length > 0 && (
          <Sheet
            sheetId="dev_db_stats"
            open
            onOpenChange={() => setFilterItems([])}
          >
            <Group borderTop={false} borderBottom={false}>
              {filterItems.map((f, _i) => (
                <XStack key={`_${f.uid}`} gap={"$medium"}>
                  <Label>{f.database}</Label>
                  <Body>{f.uid}</Body>
                </XStack>
              ))}
            </Group>
          </Sheet>
        )}
      </XStack>
    </View>
  );
});
